import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plug, Plus, Trash2, RefreshCw, Loader2, Zap, Star, ExternalLink, Check } from "lucide-react";
import { toast } from "sonner";
import { useT, useLang } from "@/lib/i18n";
import { ProviderLogo } from "@/components/ProviderLogo";

export default function Integrations() {
  const t = useT();
  const { lang } = useLang();
  const [types, setTypes] = useState({});
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ type: "azure_devops", name: "" });
  const [testing, setTesting] = useState(null);
  const [autoInvestigation, setAutoInvestigation] = useState(true);
  const [orgSettingsLoaded, setOrgSettingsLoaded] = useState(false);
  const [defaultAiId, setDefaultAiId] = useState(null);
  // AI form flow: validate key → real models → pick model (no connection, no model)
  const [aiModels, setAiModels] = useState([]);
  const [aiOk, setAiOk] = useState(null);       // null = not tested, true/false = result
  const [aiMsg, setAiMsg] = useState("");
  const [aiValidating, setAiValidating] = useState(false);
  // Claude subscription login (OAuth PKCE, no terminal)
  const [claudeSession, setClaudeSession] = useState(null);
  const [claudeCode, setClaudeCode] = useState("");
  const [claudeLoading, setClaudeLoading] = useState(false);

  const load = async () => {
    const t = await api.get("/integrations/types");
    setTypes(t.data.types);
    const i = await api.get("/integrations");
    setItems(i.data.integrations);
    try {
      const s = await api.get("/orgs/current/settings");
      setAutoInvestigation(s.data.settings?.auto_investigation ?? true);
      setOrgSettingsLoaded(true);
    } catch {/* ignore */}
    try {
      const me = await api.get("/auth/me");
      setDefaultAiId(me.data?.org?.ai_default_integration_id || null);
    } catch {/* ignore */}
  };
  useEffect(() => { load(); }, []);

  const setAsDefaultAi = async (id) => {
    try {
      await api.post(`/integrations/${id}/set-default-ai`);
      setDefaultAiId(id);
      toast.success(lang === "pt" ? "IA padrão atualizada" : lang === "es" ? "IA por defecto actualizada" : "Default AI updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const toggleAutoInvestigation = async () => {
    const next = !autoInvestigation;
    setAutoInvestigation(next);
    try {
      await api.put("/orgs/current/settings", { auto_investigation: next });
      toast.success(next
        ? (lang === "pt" ? "IA investiga automaticamente" : "AI auto-investigates")
        : (lang === "pt" ? "Investigação manual — usuário clica para iniciar" : "Manual investigation — user clicks to start"));
    } catch {
      setAutoInvestigation(!next);
      toast.error("Failed");
    }
  };

  const fieldsFor = (type) => types[type]?.fields ?? [];
  const isCredField = (f) => ["pat", "password", "secret_access_key", "token", "api_key", "app_key",
    "auth_token", "client_secret", "refresh_token", "bot_token", "webhook_url", "app_password",
    "service_account_json", "claude_oauth_token"].includes(f);
  const isPrField = (f) => f.startsWith("pr_");

  const resetAi = () => { setAiModels([]); setAiOk(null); setAiMsg(""); setClaudeSession(null); setClaudeCode(""); };
  const pickType = (type, name = "") => { setForm({ type, name, model: "" }); resetAi(); };

  const submit = async (e) => {
    e.preventDefault();
    const meta = types[form.type] || {};
    const isAi = meta.category === "ai";
    const config = {}; const credentials = {};
    for (const f of fieldsFor(form.type)) {
      if (f === "model") continue; // model is chosen from the live list, set below
      const v = form[f] ?? "";
      if (isCredField(f)) credentials[f] = v; else config[f] = v;
    }
    if (isAi) {
      if (!aiOk) { toast.error(lang === "pt" ? "Valide a conexão antes de conectar." : "Validate the connection first."); return; }
      config.model = form.model || "";
    } else if (fieldsFor(form.type).includes("model")) {
      config.model = form.model ?? "";
    }
    // AI providers can't be renamed — the name IS the provider.
    const name = isAi ? (meta.label || form.type) : form.name;
    try {
      await api.post("/integrations", { type: form.type, name, config, credentials });
      toast.success(lang === "pt" ? "Integração conectada" : "Integration connected");
      setShowNew(false); pickType("azure_devops");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const validateAiKey = async () => {
    setAiValidating(true); setAiOk(null); setAiMsg("");
    const credentials = {};
    for (const f of fieldsFor(form.type)) {
      if (f === "model") continue;
      credentials[f] = form[f] ?? "";
    }
    try {
      const { data } = await api.post("/integrations/ai/models", { type: form.type, credentials });
      setAiModels(data.models || []);
      setAiOk(!!data.ok);
      setAiMsg(data.details || "");
      if (data.ok && data.models?.length) setForm((s) => ({ ...s, model: data.models[0] }));
    } catch (e) {
      setAiOk(false); setAiMsg(e?.response?.data?.detail || "Falha");
    } finally { setAiValidating(false); }
  };

  const startClaudeLogin = async () => {
    setClaudeLoading(true);
    try {
      const { data } = await api.post("/ai/claude/login/start");
      setClaudeSession(data.session_id);
      window.open(data.auth_url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
    finally { setClaudeLoading(false); }
  };

  const submitClaudeLogin = async () => {
    if (!claudeCode.trim()) return;
    setClaudeLoading(true);
    try {
      await api.post("/ai/claude/login/submit", { session_id: claudeSession, code: claudeCode.trim() });
      toast.success(lang === "pt" ? "Claude conectado pela assinatura!" : "Claude connected via subscription!");
      setShowNew(false); pickType("azure_devops"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao trocar o código"); }
    finally { setClaudeLoading(false); }
  };

  const testIt = async (id) => {
    setTesting(id);
    try { const { data } = await api.post(`/integrations/${id}/test`); toast.success(`Connected · ${data.latency_ms}ms`); }
    catch (e) { toast.error("Test failed"); }
    finally { setTesting(null); load(); }
  };

  const del = async (id) => {
    if (!confirm(t("integ.confirmDelete"))) return;
    await api.delete(`/integrations/${id}`);
    load();
  };

  // Group types by category
  const byCategory = {};
  for (const [key, meta] of Object.entries(types)) {
    const cats = meta.categories || [meta.category || "other"];
    for (const cat of cats) {
      if (!byCategory[cat]) byCategory[cat] = [];
      byCategory[cat].push({ key, ...meta });
    }
  }
  const CATEGORY_ORDER = ["ai", "boards", "logs", "repos", "databases", "chat"];

  const formMeta = types[form.type] || {};
  const isAiForm = formMeta.category === "ai";
  const isAnthropicForm = form.type === "anthropic_claude";
  const credFields = fieldsFor(form.type).filter((f) => f !== "model");

  return (
    <div className="p-8 lg:p-12 max-w-6xl">
      <div className="overline mb-3">{t("integ.overline")}</div>
      <div className="flex justify-between items-end mb-6">
        <div>
          <h1 className="font-display font-black text-4xl tracking-tighter">{t("integ.title")}</h1>
          <p className="text-white/50 mt-2 text-sm">{t("integ.subtitle")}</p>
        </div>
        <button onClick={() => setShowNew(!showNew)} className="btn-primary text-sm py-2 px-4" data-testid="integ-new-btn">
          <Plus className="w-4 h-4" /> {t("integ.add")}
        </button>
      </div>

      {/* Auto-investigation org-wide toggle */}
      {orgSettingsLoaded && (
        <div className="surface p-4 mb-8 flex items-center justify-between gap-4" data-testid="integ-auto-investigation">
          <div className="flex items-start gap-3">
            <Zap className={`w-4 h-4 mt-0.5 ${autoInvestigation ? "text-primary" : "text-white/30"}`} />
            <div>
              <div className="text-sm font-medium">
                {lang === "pt" ? "Investigação automática" : "Auto-investigation"}
              </div>
              <div className="text-xs text-white/50 mt-0.5 max-w-xl">
                {lang === "pt"
                  ? "Quando ligado, a IA começa a análise assim que um card é criado. Quando desligado, o usuário precisa clicar em 'Investigar' no relatório do card."
                  : "When ON, AI begins analysis as soon as a card is created. When OFF, the user must click 'Investigate' on the card report."}
              </div>
            </div>
          </div>
          <button onClick={toggleAutoInvestigation}
            className={`relative w-12 h-6 transition ${autoInvestigation ? "bg-primary" : "bg-white/10"}`}
            data-testid="integ-auto-investigation-toggle">
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white transition-transform ${autoInvestigation ? "translate-x-6" : ""}`} />
          </button>
        </div>
      )}

      {showNew && (
        <form onSubmit={submit} className="surface p-6 mb-6 space-y-3" data-testid="integ-new-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1">{t("integ.type")}</label>
              <select value={form.type} onChange={(e) => pickType(e.target.value)}
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="integ-type-select">
                {CATEGORY_ORDER.map(cat => byCategory[cat] && (
                  <optgroup key={cat} label={t(`integ.category.${cat}`)}>
                    {byCategory[cat].filter(it => !it.coming_soon).map(it => <option key={it.key} value={it.key}>{it.label}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <label className="overline block mb-1">{t("common.name")}</label>
              {isAiForm ? (
                <div className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm text-white/70 flex items-center gap-2" data-testid="integ-ai-name-fixed">
                  <ProviderLogo type={form.type} size={18} />
                  <span className="truncate">{formMeta.label}</span>
                </div>
              ) : (
                <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                  placeholder="e.g. Primary"
                  className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="integ-name-input" />
              )}
            </div>
          </div>

          {isAnthropicForm && (
            <div className="border border-cyan-400/30 bg-cyan-400/5 p-3 space-y-2" data-testid="integ-claude-login">
              <div className="text-xs text-white/70">
                {lang === "pt" ? "Recomendado: entre com sua assinatura Claude (sem colar chave)."
                  : lang === "es" ? "Recomendado: entra con tu suscripción Claude (sin pegar clave)."
                  : "Recommended: sign in with your Claude subscription (no key to paste)."}
              </div>
              {!claudeSession ? (
                <button type="button" onClick={startClaudeLogin} disabled={claudeLoading} className="btn-primary text-xs py-2" data-testid="integ-claude-login-start">
                  {claudeLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
                  {lang === "pt" ? "Entrar com Claude" : "Sign in with Claude"}
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="text-xs text-white/60">
                    {lang === "pt" ? "Autorize na aba que abriu e cole o código aqui:" : "Authorize in the new tab and paste the code here:"}
                  </div>
                  <div className="flex gap-2">
                    <input value={claudeCode} onChange={(e) => setClaudeCode(e.target.value)}
                      placeholder={lang === "pt" ? "código" : "code"}
                      className="flex-1 bg-white/5 border border-white/10 px-3 py-2 text-xs font-mono" data-testid="integ-claude-code" />
                    <button type="button" onClick={submitClaudeLogin} disabled={claudeLoading || !claudeCode.trim()} className="btn-primary text-xs py-2" data-testid="integ-claude-login-submit">
                      {claudeLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      {lang === "pt" ? "Confirmar" : "Confirm"}
                    </button>
                  </div>
                </div>
              )}
              <div className="text-[11px] text-white/40">
                {lang === "pt" ? "Ou cole uma API key abaixo (pagamento por uso)." : "Or paste an API key below (pay-per-token)."}
              </div>
            </div>
          )}

          {credFields.map((f) => (
            <div key={f}>
              <label className="overline block mb-1">{f}</label>
              <input
                type={isCredField(f) ? "password" : "text"}
                value={form[f] ?? ""}
                onChange={(e) => { setForm({...form, [f]: e.target.value}); if (isAiForm) { setAiOk(null); setAiModels([]); } }}
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono"
                data-testid={`integ-field-${f}`}
              />
            </div>
          ))}

          {isAiForm && (
            <div className="space-y-2 border-t border-white/5 pt-3">
              <div className="text-[11px] text-amber-400/80">
                {lang === "pt" ? "Só um provedor de IA por vez — conectar substitui o atual."
                  : lang === "es" ? "Un proveedor de IA a la vez — conectar reemplaza el actual."
                  : "One AI provider at a time — connecting replaces the current one."}
              </div>
              <button type="button" onClick={validateAiKey} disabled={aiValidating} className="btn-ghost text-xs py-2 px-3" data-testid="integ-ai-validate">
                {aiValidating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                {lang === "pt" ? "Validar conexão" : "Validate connection"}
              </button>
              {aiOk !== null && (
                <div className={`text-xs flex items-center gap-2 ${aiOk ? "text-emerald-400" : "text-red-400"}`} data-testid="integ-ai-status">
                  <span className={`status-dot ${aiOk ? "bg-emerald-400" : "bg-red-400"}`} />
                  <span>{aiOk ? (lang === "pt" ? "Conectado" : "Connected") : (lang === "pt" ? "Falhou" : "Failed")}{aiMsg ? ` · ${aiMsg}` : ""}</span>
                </div>
              )}
              {aiOk && aiModels.length > 0 && (
                <div>
                  <label className="overline block mb-1">{lang === "pt" ? "modelo" : "model"}</label>
                  <select value={form.model || ""} onChange={(e) => setForm({...form, model: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono" data-testid="integ-ai-model">
                    {aiModels.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              )}
              {aiOk === null && (
                <div className="text-[11px] text-white/40">
                  {lang === "pt" ? "Valide a conexão para escolher o modelo." : "Validate the connection to pick a model."}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button className="btn-primary" disabled={isAiForm && !aiOk} data-testid="integ-submit">{t("common.connect")}</button>
            <button type="button" onClick={() => { setShowNew(false); resetAi(); }} className="btn-ghost">{t("common.cancel")}</button>
          </div>
        </form>
      )}

      {/* Catalog grouped by category */}
      <div className="mb-10">
        <div className="overline mb-3">{t("nav.integrations")} · {Object.keys(types).length}</div>
        <div className="space-y-6">
          {CATEGORY_ORDER.map(cat => byCategory[cat] && (
            <div key={cat}>
              <div className="overline text-primary mb-2">{t(`integ.category.${cat}`)} · {byCategory[cat].length}</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 font-mono text-xs">
                {byCategory[cat].map(it => it.coming_soon ? (
                  <div
                    key={it.key}
                    className="surface p-3 text-left flex items-center gap-2.5 opacity-50 cursor-not-allowed"
                    title={lang === "pt" ? "Em breve" : lang === "es" ? "Próximamente" : "Coming soon"}
                    data-testid={`integ-catalog-${it.key}`}
                  >
                    <ProviderLogo type={it.key} size={28} />
                    <div className="text-white/90 text-[11px] leading-snug truncate flex-1">{it.label}</div>
                    <span className="text-[9px] font-mono uppercase tracking-wider text-amber-400 border border-amber-400/30 px-1 py-0.5 shrink-0">
                      {lang === "pt" ? "em breve" : lang === "es" ? "pronto" : "soon"}
                    </span>
                  </div>
                ) : (
                  <button
                    key={it.key}
                    onClick={() => { pickType(it.key, it.label); setShowNew(true); window.scrollTo({top: 0, behavior: "smooth"}); }}
                    className="surface p-3 text-left hover:border-white/30 transition flex items-center gap-2.5"
                    data-testid={`integ-catalog-${it.key}`}
                  >
                    <ProviderLogo type={it.key} size={28} />
                    <div className="text-white/90 text-[11px] leading-snug truncate flex-1">{it.label}</div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Connected list */}
      <div className="overline mb-3">// {items.length} connected</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((it) => {
          const isAi = types[it.type]?.category === "ai";
          const isDefault = isAi && defaultAiId === it.id;
          return (
          <div key={it.id} className={`surface p-5 ${isDefault ? "border-cyan-400/60" : ""}`} data-testid={`integ-card-${it.id}`}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="overline">{types[it.type]?.category} · {it.type}</div>
                <div className="font-display font-bold text-lg mt-1 flex items-center gap-2">
                  {it.name}
                  {isDefault && (
                    <span className="text-[10px] font-mono uppercase tracking-wider text-cyan-400 inline-flex items-center gap-1" data-testid={`integ-ai-default-badge-${it.id}`}>
                      <Star className="w-3 h-3 fill-cyan-400" />
                      {lang === "pt" ? "padrão" : lang === "es" ? "por defecto" : "default"}
                    </span>
                  )}
                </div>
              </div>
              <span className={`status-dot ${it.status === "connected" ? "bg-emerald-400" : "bg-red-400"}`} />
            </div>
            <div className="font-mono text-xs text-white/40 truncate mb-4">
              {Object.entries(it.config).slice(0, 2).map(([k, v]) => `${k}=${v}`).join(" · ")}
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => testIt(it.id)} disabled={testing === it.id} className="btn-ghost text-xs py-1.5 px-3" data-testid={`integ-test-${it.id}`}>
                {testing === it.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} {t("integ.test")}
              </button>
              {isAi && !isDefault && (
                <button onClick={() => setAsDefaultAi(it.id)} className="btn-ghost text-xs py-1.5 px-3 text-cyan-400" data-testid={`integ-set-default-ai-${it.id}`}>
                  <Star className="w-3 h-3" /> {lang === "pt" ? "Tornar padrão" : lang === "es" ? "Marcar por defecto" : "Set as default"}
                </button>
              )}
              <button onClick={() => del(it.id)} className="btn-ghost text-xs py-1.5 px-3 text-red-400" data-testid={`integ-delete-${it.id}`}>
                <Trash2 className="w-3 h-3" /> {t("common.delete")}
              </button>
            </div>
          </div>
          );
        })}
        {items.length === 0 && (
          <div className="md:col-span-2 surface p-12 text-center text-white/40 text-sm">
            {t("integ.empty")}
          </div>
        )}
      </div>
    </div>
  );
}
